import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import PricingTable from "./PricingTable";
import OrangeHighlightButton from "../../elements/OrangeHighlightButton";

const PricingFrame = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || "en";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeTab, setActiveTab] = useState(0);
  const [selectedYear, setSelectedYear] = useState(1); // 0: 2025-2026, 1: 2026-2027 (default to upcoming year)

  const pricingPackages = ["english", "russian"];
  const academicYears = ["2025-2026", "2026-2027"];
  const academicYearKeys = ["year_2025_2026", "year_2026_2027"];
  const currentPackage = pricingPackages[activeTab];

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleYearChange = (
    _: React.MouseEvent<HTMLElement>,
    newValue: number | null
  ) => {
    if (newValue !== null) setSelectedYear(newValue);
  };

  const handleRedirection = () => {
    const path =
      currentLanguage === "en"
        ? "/our-program"
        : `/${currentLanguage}/our-program`;
    history.push(path);
  };

  return (
    <Box px={isMobile ? "5%" : "15%"} pt={10} pb={4}>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        textColor="secondary"
        indicatorColor="secondary"
        variant="fullWidth"
        sx={{ mb: 2 }}
      >
        {pricingPackages.map((pkgKey) => (
          <Tab
            key={pkgKey}
            label={t(`PricingPage.PricingFrame.${pkgKey}.tabLabel`)}
            sx={{
              ...theme.typography.h4,
              fontWeight: 600,
            }}
          />
        ))}
      </Tabs>

      <Box sx={{ mb: 4 }} />

      {/* Package summary — premium unified container with polished icon treatment */}
      <Box
        sx={{
          position: "relative",
          borderRadius: "24px",
          background:
            "linear-gradient(180deg, #ffffff 0%, rgba(31, 168, 223, 0.03) 100%)",
          border: `1px solid rgba(34, 74, 133, 0.08)`,
          boxShadow: "0 10px 40px rgba(34, 74, 133, 0.08)",
          p: { xs: 3, md: 5 },
          mb: 8,
          overflow: "hidden",
        }}
      >
        <Grid container spacing={{ xs: 5, md: 6 }}>
          {/* Left: package title + what's included */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              borderRight: {
                md: `1px solid rgba(34, 74, 133, 0.08)`,
              },
              pr: { md: 5 },
            }}
          >
            {/* Column header: icon badge + title */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              <Box
                sx={{
                  flexShrink: 0,
                  width: 48,
                  height: 48,
                  borderRadius: "14px",
                  backgroundColor: "#678dc9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckRoundedIcon
                  sx={{
                    fontSize: 26,
                    color: "#fff",
                  }}
                />
              </Box>
              <Typography
                variant="h5"
                fontWeight={700}
                color={theme.palette.text.secondary}
                sx={{ lineHeight: 1.2 }}
              >
                {t(`PricingPage.PricingFrame.${currentPackage}.title`)}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              color={theme.palette.text.secondary}
              sx={{ opacity: 0.65, mb: 3, ml: 0.5 }}
            >
              {t(`PricingPage.PricingFrame.${currentPackage}.subtitle`)}
            </Typography>
            <List dense disablePadding>
              {(
                t(`PricingPage.PricingFrame.${currentPackage}.included`, {
                  returnObjects: true,
                }) as string[]
              ).map((item: string, index: number) => (
                <ListItem
                  key={index}
                  disableGutters
                  sx={{ alignItems: "flex-start", py: 0.75 }}
                >
                  <ListItemIcon sx={{ minWidth: 36, mt: "2px" }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        backgroundColor: "#678dc9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CheckRoundedIcon
                        sx={{
                          fontSize: 15,
                          color: "#fff",
                          strokeWidth: 2,
                        }}
                      />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: 500,
                      lineHeight: 1.55,
                      color: theme.palette.text.secondary,
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>

          {/* Right: optional offers + CTA */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              pl: { md: 4 },
            }}
          >
            {/* Column header: icon badge + title */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              <Box
                sx={{
                  flexShrink: 0,
                  width: 48,
                  height: 48,
                  borderRadius: "14px",
                  backgroundColor: "#f89c27",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AddRoundedIcon
                  sx={{
                    fontSize: 28,
                    color: "#fff",
                  }}
                />
              </Box>
              <Typography
                variant="h5"
                fontWeight={700}
                color={theme.palette.text.secondary}
                sx={{ lineHeight: 1.2 }}
              >
                {t("PricingPage.PricingFrame.optionalTitle")}
              </Typography>
            </Box>
            {/* Invisible spacer matching left-column subtitle for list alignment */}
            <Box sx={{ mb: 3, visibility: "hidden" }}>
              <Typography variant="body2">&nbsp;</Typography>
            </Box>
            <List dense disablePadding>
              {(
                t(`PricingPage.PricingFrame.${currentPackage}.optional`, {
                  returnObjects: true,
                }) as string[]
              ).map((item, index) => (
                <ListItem
                  key={index}
                  disableGutters
                  sx={{ alignItems: "flex-start", py: 0.75 }}
                >
                  <ListItemIcon sx={{ minWidth: 36, mt: "2px" }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        backgroundColor: "#f89c27",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AddRoundedIcon
                        sx={{
                          fontSize: 15,
                          color: "#fff",
                        }}
                      />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: 500,
                      lineHeight: 1.55,
                      color: theme.palette.text.secondary,
                    }}
                  />
                </ListItem>
              ))}
            </List>
            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-start" }}>
              <OrangeHighlightButton onClick={handleRedirection}>
                {t(`PricingPage.PricingFrame.${currentPackage}.ctas.more`)}
              </OrangeHighlightButton>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Year selector — segmented pill toggle with highly obvious selected state */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mb: 5,
        }}
      >
        <ToggleButtonGroup
          value={selectedYear}
          exclusive
          onChange={handleYearChange}
          aria-label="academic year"
          sx={{
            backgroundColor: "rgba(34, 74, 133, 0.06)",
            borderRadius: "999px",
            p: 0.75,
            gap: 0.5,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {academicYears.map((year, idx) => (
            <ToggleButton
              key={year}
              value={idx}
              aria-label={t(`PricingPage.PricingFrame.academicYear.${year}`)}
              sx={{
                border: "none",
                borderRadius: "999px !important",
                px: { xs: 2.5, sm: 4 },
                py: { xs: 1.25, sm: 1.5 },
                fontSize: { xs: 13, sm: 16 },
                fontWeight: 700,
                color: theme.palette.text.secondary,
                textTransform: "none",
                letterSpacing: "0.02em",
                transition: "all 0.2s ease",
                "&.Mui-selected": {
                  backgroundColor: theme.palette.text.secondary,
                  color: theme.palette.primary.main,
                  boxShadow: "0 4px 12px rgba(34, 74, 133, 0.25)",
                  "&:hover": {
                    backgroundColor: theme.palette.text.secondary,
                  },
                },
                "&:hover": {
                  backgroundColor: "rgba(34, 74, 133, 0.1)",
                },
              }}
            >
              {t(`PricingPage.PricingFrame.academicYear.${year}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Pricing table for the selected academic year */}
      <PricingTable
        currentPackage={currentPackage}
        activeTab={activeTab}
        academicYear={academicYears[selectedYear]}
        academicYearKey={academicYearKeys[selectedYear]}
      />
    </Box>
  );
};

export default PricingFrame;
